import { useEffect, useMemo, useState } from 'react'

import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import LinkTabs from 'components/UI/LinkTabs/LinkTabs'
// import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { VaultCardsGrid } from 'features/Vault/components/VaultCardsGrid'
import { VaultDrawer } from 'features/Vault/components/VaultDrawer'
import { VaultHeader } from 'features/Vault/components/VaultHeader'
import { useCategoryExistingPosts } from 'features/Vault/hooks/useCategoryExistingPosts'
// import { useIntersectionPagination } from 'features/Vault/hooks/useIntersectionPagination'
import { refreshMyProfileCache } from 'features/Vault/hooks/useProfileRefresh'
import { useVaultBootstrap } from 'features/Vault/hooks/useVaultBootstrap'
import { useVaultFilteredMedia } from 'features/Vault/hooks/useVaultFilteredMedia'
import { useVaultHydration } from 'features/Vault/hooks/useVaultHydration'
import { useVaultInfiniteMedia } from 'features/Vault/hooks/useVaultInfiniteMedia'
import { useVaultSelection } from 'features/Vault/hooks/useVaultSelection'
import { useVaultTypeFromQuery } from 'features/Vault/hooks/useVaultTypeFromQuery'
import {
  type MediaType,
  makeAllCategory,
} from 'features/Vault/vault.constants'
import { postIdOf, vaultIdOf } from 'features/Vault/vault.helpers'
import { useFilterQuery } from 'helpers/hooks'
import {
  type VaultCategoryLite,
  buildOptimisticAddedPostEntries,
  optimisticAddPostsToVaultState,
} from 'helpers/vault/addMediaToCategory.helpers'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { AddToGroupPopUp } from 'pages/vault/components/AddToGroupPopUp'
import {
  addMediaToCategory,
  deleteFromVault,
  removeMediaFromCategory,
} from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useVaultCacheStore } from 'store/vaultCacheStore'
import { type ICategory } from 'types/interfaces/ITypes'

import style from './_vault.module.scss'

type ClientSortKey =
  | ''
  | 'most_recent'
  | 'conversion_rate'
  | 'views'
  | 'purchases'
  | 'impressions'
  | 'most_liked'
  | 'highest_tips'
  | 'most_comment'

export const Vault = () => {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()

  const fallbackUserData = useUserProfile()
  const cachedUserProfile = useProfileCacheStore(state =>
    state.getProfile(fallbackUserData?.username ?? '')
  )

  const userData = useMemo(() => cachedUserProfile ?? fallbackUserData, [cachedUserProfile, fallbackUserData])

  const { id } = useParams<any>()
  const location = useLocation()
  const stateCategoryId = location.state?.categoryId ?? null

  const filter = useFilterQuery()
  const type = filter.get('type') ?? 'all'

  // ----- local state (kept)
  const [media, setMedia] = useState<any[]>([])
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [shouldFetch, setShouldFetch] = useState(false)

  const [postsSort, setPostsSort] = useState<ClientSortKey>('')
  const [postsFilter, setPostsFilter] = useState<MediaType>('all_media')
  const [postsAsc, setPostsAsc] = useState('')
  const [queryToInvalidate, setQueryToInvalidate] = useState('vault_media')

  const [postsGrouped, setPostsGrouped] = useState<string | boolean>(true)
  const [showDuplicates, setShowDuplicates] = useState(false)
  const [priceOnly, setPriceOnly] = useState(false)
  const [selectShowDuplicates, setSelectShowDuplicates] = useState<'show_duplicates' | ''>('show_duplicates')
  const [allTimeStats, setAllTimeStats] = useState(false)
  const [showPaidOnly, setShowPaidOnly] = useState(false)
  const [unreleasedOnly, setUnreleasedOnly] = useState(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [addToActive, setAddToActive] = useState(false)

  const [isHydrated, setIsHydrated] = useState(false)

  const ALL_CATEGORY: ICategory = useMemo(() => makeAllCategory(), [])
  const [activeCategory, setActiveCategory] = useState<any[]>([ALL_CATEGORY])

  // ---- selection hook (includes your big toggleSelectedPosts unchanged)
  const selection = useVaultSelection(postsGrouped)

  const {
    isGrouped,
    selectedPosts,
    setSelecetedPosts,
    postIds,
    setPostIds,
    allSelectedMedia,
    setAllSelectedMedia,
    selectedVariantKeys,
    setSelectedVariantKeys,
    allMediaLength,
    setAllMediaLength,
    toggleVariantSelection,
    toggleSelectedPosts,
  } = selection

  const selectedCategoryId = activeCategory?.[0]?.id ?? null

  // vaultKey (unchanged logic, just isolated)
const vaultKey = useMemo(() => {
  const uid = userData?.id ?? ''
  const tab = activeFilter || 'all'
  const sortK = postsSort || ''
  const sortDir = postsAsc || ''
  const dupe = showDuplicates ? 'dup' : 'nodup'
  const statsKey = allTimeStats ? 'alltime' : 'default'
  const priceKey = priceOnly ? 'price' : 'allprice'
  const unreleasedKey = unreleasedOnly ? 'unreleased' : 'releasedall'
  const mediaTypeKey = postsFilter || 'all_media'
  const modeKey = isGrouped
    ? selectedCategoryId === null
      ? 'g:all'
      : `g:${selectedCategoryId}`
    : 'ungrouped'

return `${uid}-${tab}-${sortK}-${sortDir}-${mediaTypeKey}-${modeKey}-${dupe}-${statsKey}-${priceKey}-${unreleasedKey}`}, [
  userData?.id,
  activeFilter,
  postsSort,
  postsAsc,
  postsFilter,
  showDuplicates,
  unreleasedOnly,
  allTimeStats,
  isGrouped,
  selectedCategoryId,
  priceOnly,
])

  // ---- cache store
  const { getVaultMedia, setVaultMedia } = useVaultCacheStore()
  const queryClient = useQueryClient()

  useEffect(() => setTitle('Shared Media'), [setTitle])


  // reset sort/filter when tab changes (same as you had)
  useEffect(() => {
    setPostsSort('')
    setPostsFilter('all_media')
    setPostsAsc('')
  }, [activeFilter])

  // map URL type -> activeFilter (extracted)
  useVaultTypeFromQuery(type, setActiveFilter)

  // bootstrap activeCategory + shouldFetch (extracted)
  useVaultBootstrap({
    categories: userData?.all_media_categories ?? [],
    idParam: id,
    stateCategoryId,
    ALL_CATEGORY,
    setActiveCategory,
    setShouldFetch,
  })

  // hydrate list from cache (deduped + extracted)
  useVaultHydration({
    vaultKey,
    getVaultMedia,
    setMedia,
    setIsHydrated,
  })

  // infinite query (extracted)
  const {
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useVaultInfiniteMedia({
    vaultKey,
    activeFilter,
    shouldFetch,
    activeCategory,
    selectedCategoryId,
    allTimeStats,
    isGrouped,
    showDuplicates,
    unreleasedOnly,
    postsSort,
    postsAsc,
    postsFilter,
    setMedia,
    setVaultMedia,
    priceOnly,
  })

  // filteredMedia + shouldPaginate extracted
  const { filteredMedia, shouldPaginate } =
    useVaultFilteredMedia({
      media,
      activeFilter,
      postsFilter,
      unreleasedOnly,
      showPaidOnly,
      postsGrouped,
      activeCategory,
    })
  // intersection observer extracted
/*   const { lastPostRef } = useIntersectionPagination({
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    shouldPaginate,
  }) */

  // categoryExistingPosts extracted
const [categoryExistingPosts, setCategoryExistingPosts] =
  useState<string[]>([])
  useCategoryExistingPosts({
    media,
    activeCategory,
    postsGrouped,
    setCategoryExistingPosts,
  })

  // open/close drawer based on selection
  useEffect(() => {
    setDrawerOpen(allSelectedMedia.length > 0)
  }, [allSelectedMedia])

  // clear selection when these change (same as you had)
  useEffect(() => {
    setAllSelectedMedia([])
    setSelecetedPosts([])
    setPostIds([])
    setAllMediaLength(0)
    setSelectedVariantKeys([])
  }, [
    activeFilter,
    activeCategory,
    postsGrouped,
    postsAsc,
    postsFilter,
    postsSort,
    showDuplicates,
    priceOnly,
  ])


  const addMediaMutation = useMutation(
    async (vars: {
      id: string
      posts: string[]
      messages: string[]
      stories: string[]
    }) =>
      await addMediaToCategory(
        vars.id,
        vars.posts,
        vars.messages,
        vars.stories
      )
  )

  const removeMediaMutation = useMutation(
    async () => {
      const postIdsToRemove = postIds.filter((pid: string) =>
        allSelectedMedia.some(
          (item: any) =>
            item.post &&
            postIdOf(item) === pid
        )
      )

      const messageVaultIdsToRemove = selectedPosts.filter((id: string) =>
        allSelectedMedia.some(
          (item: any) =>
            item.id === id &&
            item.message?.message
        )
      )

      return await removeMediaFromCategory(
        activeCategory[0].id,
        postIdsToRemove,
        messageVaultIdsToRemove
      )
    },
    {
      onSuccess: async () => {
        setAllSelectedMedia([])
        setPostIds([])

        queryClient.invalidateQueries({
          queryKey: ['vaultMedia', vaultKey]
        })

        await refreshMyProfileCache()

        queryClient.invalidateQueries(queryToInvalidate)
      },
      onError: () => {
        addToast('error', 'An error occurred while removing media.')
      }
    }
  )

const deleteMediaMutation = useMutation(
  async () => {
    const payload: string[] = allSelectedMedia
      .map((item: any) =>
        item.post?.id ??
        item.message?.message?.id ??
        item.story?.id
      )
      .filter((id: any): id is string =>
        typeof id === 'string' && id.length > 0
      )

    console.log('[VAULT DELETE]', {
      allSelectedMedia,
      payload,
    })

    return await deleteFromVault(payload)
  },
  {
    onSuccess: () => {
      const deletedIds = new Set<string>(
        allSelectedMedia
          .map((item: any) =>
            item.post?.id ??
            item.message?.message?.id ??
            item.story?.id
          )
          .filter((id: any): id is string =>
            typeof id === 'string' && id.length > 0
          )
      )

      setMedia((prev: any[]) =>
        Array.isArray(prev)
          ? prev.filter(item => {
              const id =
                item.post?.id ??
                item.message?.message?.id ??
                item.story?.id

              return !deletedIds.has(String(id))
            })
          : []
      )

      setAllSelectedMedia([])
      setPostIds([])

      queryClient.invalidateQueries('allMediaCategories')
      queryClient.invalidateQueries('vaultMedia')
      queryClient.invalidateQueries('allVaultMediaGrouped')
      queryClient.invalidateQueries(queryToInvalidate)
    },
    onError: () => {
      addToast('error', 'An error occurred, please try again.')
    },
  }
)
  // helpers for drawer props
  const onSwitchToGrouped = () => {
    setPostsGrouped(true)
    setShowDuplicates(true)
    setSelectShowDuplicates('show_duplicates')
  }

// HIDE FOOTER/HEADER WHEN SCROLLING DOWN
useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false

  const SHOW_AT_TOP_PX = 20
  const HIDE_AFTER_SCROLL_PX = 80
  const DELTA_PX = 8

  const setChromeHidden = (hidden: boolean) => {
    document.body.classList.toggle('footer-hidden', hidden)
    document.body.classList.toggle('header-hidden', hidden)
  }

  const onScroll = () => {
    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

      requestAnimationFrame(() => {
        ticking = false

        if (top <= SHOW_AT_TOP_PX || top < HIDE_AFTER_SCROLL_PX) {
          setChromeHidden(false)
          lastTop = top
          return
        }

        if (Math.abs(delta) < DELTA_PX) {
          lastTop = top
          return
        }

        setChromeHidden(delta > 0)
        lastTop = top
      })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  setChromeHidden(window.scrollY > HIDE_AFTER_SCROLL_PX)

  return () => {
    window.removeEventListener('scroll', onScroll)
    setChromeHidden(false)
  }
}, [])



  const userCategories = (userData?.all_media_categories ?? []) as ICategory[]

  return (
    <>
      <WithHeaderSection
        customClass='vault_wrapper'
        headerSection={
          <VaultHeader
            type={type}
            id={id}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            postsGrouped={postsGrouped}
            postsFilter={postsFilter}
            showDuplicates={showDuplicates}
            unreleasedOnly={unreleasedOnly}
            // showPaidOnly={showPaidOnly}
            priceOnly={priceOnly}
            setPriceOnly={setPriceOnly}
            postsSort={postsSort}
            postsAsc={postsAsc}
            setPostsSort={setPostsSort}
            setPostsAsc={setPostsAsc}
            setPostsGrouped={setPostsGrouped}
            setPostsFilter={setPostsFilter}
            setSelectShowDuplicates={setSelectShowDuplicates}
            setShowDuplicates={setShowDuplicates}
            allTimeStats={allTimeStats}
            setAllTimeStats={setAllTimeStats}
            // setShowPaidOnly={setShowPaidOnly}
            setUnreleasedOnly={setUnreleasedOnly}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            userData={userData}
          />
        }
      >
        <div className={style.vault__header}>
          <LinkTabs
            route={!id ? '/vault' : `/vault/${id}`}
            filters={['all', 'feed', 'messages', 'reels', 'story']}
            activeFilter={
              activeFilter === 'mass_messages'
                ? 'messages'
                : activeFilter
            }
            customClass="vault"
          />
        </div>
        <VaultCardsGrid
          drawerOpen={drawerOpen}
          isLoading={isLoading}
          isHydrated={isHydrated}
          fetchNextPage={fetchNextPage}
          isFetching={isFetching}
          filteredMedia={filteredMedia}
          toggleSelectedPosts={toggleSelectedPosts}
          allSelectedMedia={allSelectedMedia}
          selectedPosts={selectedPosts}
          postsGrouped={postsGrouped}
          showDuplicates={showDuplicates}
          postsFilter={postsFilter}
          postsSort={postsSort}
          allTimeStats={allTimeStats}
          
          onSelectVariant={toggleVariantSelection}
          isVariantSelected={(key: string) => selectedVariantKeys.includes(key)}
          getVariantIndex={(key: string) => {
            const i = selectedVariantKeys.indexOf(key)
            return i === -1 ? null : i + 1
          }}
          shouldPaginate={shouldPaginate}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          // lastPostRef={lastPostRef}
        />

        <VaultDrawer
          drawerOpen={drawerOpen}
          isGrouped={isGrouped}
          onSwitchToGrouped={onSwitchToGrouped}
          activeCategory={activeCategory}
          navigate={navigate}
          allSelectedMedia={allSelectedMedia}
          selectedPosts={selectedPosts}
          postIds={postIds}
          setSelecetedPosts={setSelecetedPosts}
          setAllSelectedMedia={setAllSelectedMedia}
          setPostIds={setPostIds}
          deleteMedia={() => deleteMediaMutation.mutate()}
          removeMedia={() => removeMediaMutation.mutate()}
          addMediaToCategory={(payload) => addMediaMutation.mutate(payload)}
          userCategories={userCategories}
        />

        {activeCategory[0] && activeCategory[0].name !== 'All' && !addToActive && (
          <div
            className={style.add_to_category_button}
            onClick={() => setAddToActive(true)}
          >
            <spriteIcons.IconPlus color='#ffffff' />
          </div>
        )}

        {activeCategory[0] && activeCategory[0].name !== 'All' && (
          <AddToGroupPopUp
            initialCategory={{
              name: 'All',
              id: null,
              selected: true,
              posts: 0,
              premium: 0,
              stories: 0,
              messages: 0,
            }}
            selectedCategory={activeCategory[0]}
            selectedCategoryPostIds={categoryExistingPosts}
            submitFn={(allSelectedMediaArg, selectedPostsArg) => {
              const catId = activeCategory[0]?.id as string
              const catName = String(activeCategory[0]?.name ?? '')

              if (!catId) {
                addToast('error', 'No category selected')
                return
              }

              const category: VaultCategoryLite = {
                id: catId,
                name: catName,
              }

              const posts = selectedPostsArg
                .filter((id: string) =>
                  allSelectedMediaArg.some(
                    (item: any) => item.id === id && item.post
                  )
                )
                .map((id: string) => {
                  const found = allSelectedMediaArg.find(
                    (item: any) => item.id === id
                  )

                  return found?.post?.id ?? id
                })

              const patchedPostEntries =
                buildOptimisticAddedPostEntries(
                  allSelectedMediaArg,
                  category
                )

              const prevMedia = media
              const prevCache = getVaultMedia(vaultKey)

              const { nextMedia, nextCache } =
                optimisticAddPostsToVaultState({
                  currentMedia: prevMedia,
                  currentCache: prevCache as any,
                  vaultKey,
                  patchedPostEntries,
                })

              setMedia(nextMedia)
              setVaultMedia(vaultKey, nextCache)

              setAddToActive(false)
              setSelecetedPosts([])
              setAllSelectedMedia([])

              addMediaMutation.mutate(
                {
                  id: catId,
                  posts,
                  messages: [],
                  stories: [],
                },
                {
                  onError: () => {
                    setMedia(prevMedia)
                    setVaultMedia(vaultKey, prevCache as any)
                    addToast(
                      'error',
                      'An error occured, please try again.'
                    )
                  },
                }
              )
            }}
            cancelFn={() => {
              setAddToActive(false)
              setSelecetedPosts([])
              setAllSelectedMedia([])
            }}
            open={addToActive}
            loading={addMediaMutation.isLoading}
            setQueryToInvalidate={setQueryToInvalidate}
          />
        )}

        <div style={{ paddingTop: '1rem' }} />
      </WithHeaderSection>

      <DesktopAdditionalContent />
    </>
  )
}
